import { FC } from 'react';
import { Button, Space, Tag } from 'antd';
import { TbDatabase, TbPlayerPlay, TbTable, TbVariable } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { EmptySection } from '@/shared';
import { PlatformAlert } from '@/shared/components/alerts';
import { SetStateMethod } from '@/types';

import type { VantageMetadata } from '../../../../../types';
import { CollapsibleSection } from '../VantageSettingsPrimitives';
import { buildTablePreviewSql, humanize, TABLE_PRESENTATION } from '../VantageSettingsPrimitives/helpers';

interface QueriesTabProps {
    insertAtCursor: (value: string) => void;
    limit: number;
    metadata: VantageMetadata;
    metadataError: string | null;
    setCode: SetStateMethod<string>;
}

const QueriesTab: FC<QueriesTabProps> = ({ insertAtCursor, limit, metadata, metadataError, setCode }) => {
    const { token } = useAntDesignTheme();

    return (
        <div>
            {metadataError ? (
                <PlatformAlert type='warning' showIcon message={metadataError} style={{ margin: '1rem' }} />
            ) : null}
            <CollapsibleSection
                title='Dynamic Variables'
                description='Insert runtime values directly at the editor cursor.'
                icon={<TbVariable />}
            >
                {metadata.variables.length ? (
                    <Space wrap size={[6, 6]}>
                        {metadata.variables.map((variable) => {
                            const expression = `{{ ${variable.key} }}`;
                            return (
                                <Tag
                                    key={variable.key}
                                    onClick={() => insertAtCursor(expression)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {expression}
                                    <span style={{ color: token.colorTextTertiary }}> {variable.type}</span>
                                </Tag>
                            );
                        })}
                    </Space>
                ) : (
                    <EmptySection
                        height={128}
                        icon={<TbVariable size='1.25rem' />}
                        label='No Dynamic Variables'
                        description='Workspace variables will appear here when available.'
                    />
                )}
            </CollapsibleSection>
            <CollapsibleSection
                title='Schema'
                description='Explore accessible columns; sensitive fields are never exposed to Vantage.'
                icon={<TbDatabase />}
            >
                {metadata.tables.length ? (
                    metadata.tables.map((table) => {
                        const presentation = TABLE_PRESENTATION[table.name] ?? {
                            description: `Accessible ${humanize(table.name)} metadata.`,
                            icon: <TbTable />,
                        };
                        return (
                            <CollapsibleSection
                                key={table.name}
                                title={humanize(table.name)}
                                description={presentation.description}
                                icon={presentation.icon}
                                defaultOpen={false}
                            >
                                <Space wrap size={[6, 6]}>
                                    {table.columns.map((column) => (
                                        <Tag key={column.name}>
                                            {column.name}
                                            <span style={{ color: token.colorTextTertiary }}> {column.type}</span>
                                        </Tag>
                                    ))}
                                </Space>
                                <div style={{ marginTop: '0.75rem' }}>
                                    <Button
                                        size='small'
                                        icon={<TbPlayerPlay />}
                                        onClick={() => setCode(buildTablePreviewSql(table.name, limit))}
                                    >
                                        Load Query
                                    </Button>
                                </div>
                            </CollapsibleSection>
                        );
                    })
                ) : (
                    <EmptySection
                        height={128}
                        icon={<TbTable size='1.25rem' />}
                        label='No Schema Available'
                        description='Accessible tables will appear once metadata is available.'
                    />
                )}
            </CollapsibleSection>
        </div>
    );
};

export default QueriesTab;
